// pages/Oati/OattIngestionLogsTable.tsx
import { useState } from 'react';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { useQuery } from '@apollo/client';
import { Table, Empty, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { GET_OATT_FILE_INGESTION_LOGS } from '@graphql/oatt.gql';
import { formatToAtlanticTime } from '../../utils/dateFormatter';
import styles from './OattPages.module.css';

interface OattIngestionLog {
  oattFileIngestionLogs_id: number;
  file_name: string;
  record_count: number | null;
  status: string;
  month_year: string | null;
  ingestion_timestamp: string;
  upload_by: string | null;
  error_message: string | null;
}

interface LogRow extends OattIngestionLog {
  key: number;
  upload_time_formatted: string;
}

const renderFileStatus = (status: string) => {
  if (!status) return <span>-</span>;
  const normalized = status.toLowerCase();
  const map: Record<string, { icon: string; label: string; cls: string }> = {
    success: { icon: '✅', label: 'Success', cls: styles.statusSuccess },
    failed: { icon: '❌', label: 'Failed', cls: styles.statusFailed },
  };
  const entry = map[normalized] ?? { icon: '', label: status, cls: '' };
  return (
    <span className={`${styles.statusText} ${entry.cls}`}>
      {entry.icon} {entry.label}
    </span>
  );
};

const columns: ColumnsType<LogRow> = [
  {
    title: 'File Name',
    dataIndex: 'file_name',
    key: 'file_name',
    ellipsis: true,
  },
  {
    title: 'Meter Count',
    dataIndex: 'record_count',
    key: 'record_count',
    render: (v) => (v != null ? v : '-'),
  },
  {
    title: 'File Status',
    dataIndex: 'status',
    key: 'status',
    render: renderFileStatus,
  },
  {
    title: 'Month-Year',
    dataIndex: 'month_year',
    key: 'month_year',
    render: (v) => v || '-',
  },
  {
    title: 'Upload Time',
    dataIndex: 'upload_time_formatted',
    key: 'upload_time',
  },
  {
    title: 'Upload By',
    dataIndex: 'upload_by',
    key: 'upload_by',
    render: (v) => v || '-',
  },
];

const OattIngestionLogsTable = () => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { data, loading, error } = useQuery<{ oattFileIngestionLogs: OattIngestionLog[] }>(
    GET_OATT_FILE_INGESTION_LOGS,
    { fetchPolicy: 'cache-and-network' }
  );

  const rows: LogRow[] = (data?.oattFileIngestionLogs ?? []).map((r) => ({
    ...r,
    key: r.oattFileIngestionLogs_id,
    upload_time_formatted: formatToAtlanticTime(r.ingestion_timestamp, 'full'),
  }));

  return (
    <div className={styles.logsSection}>

      {error ? (
        <Empty description={`Error loading logs: ${error.message}`} />
      ) : (
        <Table<LogRow>
          dataSource={rows}
          columns={columns}
          loading={loading ? { indicator: <Spin size="large" /> } : false}
          rowKey="oattFileIngestionLogs_id"
          pagination={{
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            onShowSizeChange: (_, size) => setPageSize(size),
            showTotal: (total) => `Total ${total} records`,
          }}
          scroll={{ x: 'max-content' }}
          expandable={{
            // Show the error detail only for rows that have an error_message
            expandedRowRender: (record) => (
              <div className={styles.expandRow}>
                <strong>Error Details: </strong>
                {record.error_message}
              </div>
            ),
            rowExpandable: (record) => !!record.error_message,
            expandIcon: ({ expanded, onExpand, record }) => {
              if (!record.error_message) {
                // Keep column width uniform on rows that cannot be expanded
                return <span data-testid="expand-icon-placeholder" className={styles.expandIconPlaceholder} />;
              }
              return (
                <button
                  type="button"
                  className={styles.expandBtn}
                  onClick={(e) => onExpand(record, e)}
                  aria-label={expanded ? 'Collapse error details' : 'Expand error details'}
                >
                  {expanded ? <MinusOutlined /> : <PlusOutlined />}
                </button>
              );
            },
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No file ingestion logs found"
              />
            ),
          }}
        />
      )}
    </div>
  );
};

export default OattIngestionLogsTable;
